import {
  addDays,
  addWeeks,
  startOfWeek,
  differenceInWeeks,
  eachDayOfInterval,
} from 'date-fns';

import { utcToZonedTime, format } from 'date-fns-tz';
import moment from 'moment';

export const calculateWeek = (offset = 0) => {
  const start = startOfWeek(addWeeks(new Date(), offset), {
    weekStartsOn: 1,
  });

  const days = eachDayOfInterval({ start, end: addDays(start, 6) });

  return days;
};

export const findWeekOffset = (weekArray: Date[]) => {
  if (weekArray.length === 0) {
    return 0;
  }

  const providedWeekStart = weekArray[0];
  const currentWeekStart = startOfWeek(new Date(), { weekStartsOn: 1 });

  const offset = differenceInWeeks(providedWeekStart, currentWeekStart);

  return offset;
};

export const toMidnightISOString = (
  date: Date | undefined,
): string | undefined => {
  if (!date) return undefined;

  const day = date.getDate();
  const month = date.getMonth() + 1; // getMonth() is zero-based
  const year = date.getFullYear();

  return `${year}-${month
    .toString()
    .padStart(2, '0')}-${day.toString().padStart(2, '0')}T00:00:00.000Z`;
};

/**
 * Date and Time Handling between FrontEnd and BackEnd:
 *
 * Due to the distributed nature of web applications, servers and clients often exist in different time zones. This can lead to discrepancies and confusion when working with date and time values.
 *
 * 1. UTC (Coordinated Universal Time) is used as a standard reference point. By converting all date-times to UTC before storage or transmission, we ensure a consistent basis for all date-time values, regardless of the server's or client's location.
 *
 * 2. When the FrontEnd (FE) sends a date-time to the BackEnd (BE):
 *    - The FE converts the local date-time to UTC.
 *    - This UTC value is then sent to the BE for processing or storage.
 *
 * 3. When the BE sends a date-time to the FE:
 *    - The BE sends the date-time in its stored UTC format.
 *    - Upon receipt, the FE converts this UTC date-time back to the local date-time of the client for display or further processing.
 *
 * This process ensures that:
 *    - Date-time values remain consistent and unambiguous across various time zones.
 *    - The user always interacts with date-times that are relevant to their local context.
 *    - The server deals with a standardized UTC date-time, ensuring consistent behavior and storage.
 *
 * Libraries like `date-fns` and `date-fns-tz` are used to facilitate these conversions, ensuring accuracy and handling edge cases (like Daylight Saving Time shifts).
 */

/**
 * Converts a local date object to a UTC string for sending to the API.
 */
export function convertLocalToUTC(date: Date): Date {
  const utcDate = utcToZonedTime(date, 'Etc/UTC');

  return utcDate;
}

/**
 * Transforms a UTC date string into a local Date object, effectively treating it as a date without a timezone.
 *
 * Steps:
 * 1. Converts the input UTC date string into a Date object. This initial Date object is in the UTC time zone.
 * 2. Converts this UTC Date object to an ISO string, which is also in UTC.
 * 3. Removes the 'Z' character from the end of the ISO string, which indicates UTC time zone.
 *    This results in a time-zone-agnostic string.
 * 4. Creates a new Date object from this modified string, which is then interpreted in the local time zone.
 *
 * @param {string} utcString - The UTC date string to be converted.
 * @returns {Date} - The resulting local Date object.
 */
export function transformUTCStringToLocalDate(utcString: string | Date): Date {
  // Step 1: Convert the UTC string to a Date object (UTC Date)
  const utcDate = new Date(utcString);

  // Step 2: Convert the UTC Date object to an ISO string (still in UTC)
  const isoString = utcDate.toISOString();

  // Step 3: Remove the 'Z' character to make the string time-zone-agnostic
  const timeZoneAgnosticString = isoString.slice(0, -1);

  // Step 4: Create a new Date object using the modified string (interpreted as local time)
  const localDate = new Date(timeZoneAgnosticString);

  return localDate;
}

/**
 * Transforms a UTC date into a local Date object, effectively treating it as a date without a timezone.
 * @param {Date} utcDate - The UTC date  to be converted.
 * @returns {Date} - The resulting local Date object.
 */
export function transformUTCDateToLocalDate(utcString: Date): Date {
  const isoString = utcString.toISOString();
  const localDate = new Date(isoString);
  return localDate;
}

/**
 * Converts a UTC string received from the API to a local date object for display.
 */
export function convertUTCToLocal(
  utcString?: string | Date,
  shouldFormat?: boolean,
  onlyDate?: boolean,
  dateFormat?: string,
): string | Date {
  try {
    if (!utcString) {
      return '-';
    }

    // // Convert the UTC string to a Date object first
    // const utcDate = toDate(utcString, {
    //   timeZone: 'UTC',
    // });

    // const localDate = utcToZonedTime(
    //   utcDate,
    //   Intl.DateTimeFormat().resolvedOptions().timeZone,
    // );

    const localDate = moment.utc(utcString).local().toDate();

    if (shouldFormat) {
      const defaultDateFormat = onlyDate ? 'MM/DD/yyyy' : 'MM/DD/yyyy hh:mm';
      const finalDateFormat = dateFormat || defaultDateFormat;
      return moment.utc(utcString).format(finalDateFormat);
    }

    return localDate;
  } catch (error) {
    return '';
  }
}

export function toApiPayloadDateFormat(date: string | Date) {
  if (!date) {
    return '';
  }

  try {
    let result: string;

    if (typeof date === 'string') {
      // For string dates (from FormikDatepicker), treat as local date to avoid timezone issues
      result = moment(date).format('YYYY-MM-DD');
    } else {
      // For Date objects, use UTC
      const dateString = date.toISOString();
      result = moment.utc(dateString).format('YYYY-MM-DD');
    }

    return result;
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
}
